import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import PageArea from '../../components/shared/PageArea';
import { Section } from '../../components/ui/Section';
import { PictureTile } from '../../components/shared/tiles/PictureTile';
import { ArrowLeft } from '../../assets';
import { recipeV2Client } from '../../app/services/api/recipeV2';
import { RecipeV2Detail } from './types';
import RecipeBankIngredientsTab from './components/RecipeBankIngredientsTab';
import RecipeBankMethodTab from './components/RecipeBankMethodTab';
import RecipeBankAllergensTab from './components/RecipeBankAllergensTab';
import RecipeBankDietaryTab from './components/RecipeBankDietaryTab';
import RecipeBankNutritionTab from './components/RecipeBankNutritionTab';
import RecipePublishDialog from './components/RecipePublishDialog';
import RecipeAudiencePreviewDialog from './components/RecipeAudiencePreviewDialog';
import { UserContext } from '../../components/shared/useUser';

/** Single-scroll grouping matching the section headings, not the tabbed authoring layout. */
const RecipeBankSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Section style={{ p: 5 }}>
    <Typography variant="textMD"
      fontWeight={600}
      sx={{ mb: 4 }}>{title}</Typography>
    {children}
  </Section>
);

/**
 * Recipe Bank detail: a read-only operational view over an Active Recipe V2 recipe,
 * presented as stacked sections (matching the Recipe Bank browsing flow) rather than the
 * authoring Recipe V2 detail page's tabs. No edit/delete/approval/rollback/version-
 * management/cost affordances - see #789 scope.
 */
const RecipeBankDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('recipeV2');
  const recipeId = Number(id);
  const { hasPermissionTo } = useContext(UserContext);
  const canPublish = hasPermissionTo(['RecipeV2Read']) && hasPermissionTo(['RecipeV2Export']);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [audiencePreviewOpen, setAudiencePreviewOpen] = useState(false);

  const [recipe, setRecipe] = useState<RecipeV2Detail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (Number.isNaN(recipeId)) {
      setError('not-found');
      setIsLoading(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    recipeV2Client.getRecipeBankDetail(recipeId)
      .then((value) => { if (active) setRecipe(value); })
      .catch((cause) => {
        if (active) {
          setRecipe(null);
          setError(cause instanceof Error ? cause.message : 'not-found');
        }
      })
      .finally(() => { if (active) setIsLoading(false); });

    return () => { active = false; };
  }, [recipeId]);

  if (error) {
    return (
      <Box sx={{ p: 5 }}>
        <Alert severity="error">{t('notFound')}</Alert>
      </Box>
    );
  }

  if (isLoading || !recipe) {
    return (
      <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 5 }}>
        <IconButton
          onClick={() => navigate('/range/recipe-bank')}
          aria-label={t('recipeBank.backToBank', 'Back to Recipe Bank')}
        >
          <ArrowLeft style={{ width: 20, height: 20 }} />
        </IconButton>
        <Typography variant="textXL"
          fontWeight={700}>{recipe.description}</Typography>
        {recipe.primaryCategory && (
          <Chip label={recipe.primaryCategory}
            size="small" />
        )}
        <Button
          variant="secondary"
          startIcon={<VisibilityOutlined />}
          onClick={() => setAudiencePreviewOpen(true)}
          sx={{ ml: 'auto' }}
        >
          {t('recipeBank.customerView', 'Customer view')}
        </Button>
        {canPublish && (
          <Button
            variant="secondary"
            startIcon={<PictureAsPdfOutlined />}
            onClick={() => setPublishDialogOpen(true)}
          >
            {t('publishing.exportRecipe', 'Export recipe')}
          </Button>
        )}
      </Stack>

      <Box sx={{ height: { xs: 220, md: 340 }, mb: 6 }}>
        <PictureTile
          src={recipe.mediaUrl ?? null}
          alt={recipe.description}
          objectFit="cover"
          fallbackLabel={t('recipeBank.noImage', 'No recipe image')}
          fallback={(
            <Stack alignItems="center"
              spacing={1}>
              <ImageOutlined sx={{ fontSize: 40 }} />
              <Typography variant="textSM">{t('recipeBank.noImage', 'No recipe image')}</Typography>
            </Stack>
          )}
          sx={{ height: '100%' }}
        />
      </Box>

      {recipe.narrativeDescription && (
        <Typography color="text.secondary"
          sx={{ mb: 6 }}>{recipe.narrativeDescription}</Typography>
      )}

      <Stack spacing={5}>
        <RecipeBankSection title={t('tabs.ingredients')}>
          <RecipeBankIngredientsTab components={recipe.components ?? []} />
        </RecipeBankSection>
        <RecipeBankSection title={t('tabs.allergens')}>
          <RecipeBankAllergensTab allergens={recipe.allergenSummary ?? []} />
        </RecipeBankSection>
        <RecipeBankSection title={t('tabs.dietary')}>
          <RecipeBankDietaryTab attributes={recipe.dietaryAttributes ?? []} />
        </RecipeBankSection>
        <RecipeBankSection title={t('tabs.nutrition')}>
          <RecipeBankNutritionTab nutrition={recipe.nutrition ?? []} />
        </RecipeBankSection>
        <RecipeBankSection title={t('tabs.method')}>
          <RecipeBankMethodTab steps={recipe.steps ?? []}
            productionMethodName={recipe.productionMethod?.name} />
        </RecipeBankSection>
      </Stack>

      <RecipeAudiencePreviewDialog
        open={audiencePreviewOpen}
        recipeId={recipe.id}
        onClose={() => setAudiencePreviewOpen(false)}
      />
      <RecipePublishDialog
        open={publishDialogOpen}
        recipe={recipe}
        onClose={() => setPublishDialogOpen(false)}
      />
    </PageArea>
  );
};

export default RecipeBankDetailPage;
